import mountFarloRangeCalendar from './rangeCalendar';
import {
	initFilterBar,
	getInlineObj,
	paintChoiceActives,
	wireCalendarEnable,
	markScopeDirty,
	updateAllFiltersCount,
	updateClearChip,
	bindPriceControls,
	toQS,
	cleanParams,
	adjustPopoverPosition
} from './filtersCore';
import { initShowCardTabs } from './ticketBoothTabs';
import mountShowSearch from './showSearch';
import { createFilterTracker } from './filterAnalytics';

function snapshotPanel(panel) {
	if (!panel) {
		return;
	}
	const snap = {
		checks: Array.from(panel.querySelectorAll('input[type="checkbox"], input[type="radio"]'))
			.map((i) => ({ name: i.name, value: i.value, checked: i.checked })),
		fields: ['from', 'to', 'max'].map((n) => {
			const el = panel.querySelector(`[name="${ n }"]`);
			return { name: n, value: el ? el.value : '' };
		})
	};
	panel.__snap = snap;
}

function restorePanel(panel) {
	const s = panel?.__snap;
	if (!s) {
		return;
	}
	s.checks.forEach(({ name, value, checked }) => {
		panel.querySelectorAll(`input[name="${ CSS.escape(name) }"]`).forEach((i) => {
			if (i.type === 'radio') {
				i.checked = (i.value === value) && checked;
			} else if (i.type === 'checkbox' && i.value === value) {
				i.checked = checked;
			}
		});
	});
	s.fields.forEach(({ name, value }) => {
		const el = panel.querySelector(`[name="${ CSS.escape(name) }"]`);
		if (el) {
			el.value = value;
		}
	});
}

export default function ticketBoothFilters() {
	const bar = document.querySelector('[data-ticketbooth-filterbar]');
	if (!bar) {
		return;
	}

	const form = bar.closest('form');
	const list = document.querySelector('[data-results]');
	const endpoint = bar.getAttribute('data-endpoint');
	const sentinel = document.querySelector('[data-lazy-load-sentinel]');

	if (!form || !list || !endpoint) {
		return;
	}
	if (!sentinel) {
		console.warn('Ticketbooth: Lazy load sentinel not found');
	}

	// 'shows' is included in MULTI so getInlineObj() collects multiple
	// hidden inputs with name="shows[]" (created by the show search chips) into an array.
	const MULTI = new Set(['time', 'genre', 'shows']);

	const DEFAULTS = {
		q: '', from: '', to: '', today: '', tomorrow: '', access: '',
		max: '', time: '', genre: '', shows: [], sort: 'name_asc'
	};

	const CLEAR_DEFAULTS = {
		...DEFAULTS,
		today: '1'
	};

	const SORT_MAP = {
		'name-atz': 'name_asc', 'name-zta': 'name_desc',
		'date-old': 'date_old', 'date-new': 'date_new',
		'price-low': 'price_low', 'price-high': 'price_high'
	};
	const normaliseSort = (v) => SORT_MAP[ v ] || v || 'name_asc';

	const setTrigger = (key, on) => {
		bar.querySelectorAll(`[data-open="${ key }"]`).forEach((btn) => {
			btn.classList.toggle('is-active', !!on);
			btn.setAttribute('aria-pressed', on ? 'true' : 'false');
		});
	};

	const isDateActive = () => !!(form.querySelector('input[name="from"]')?.value || form.querySelector('input[name="to"]')?.value);
	const isPriceActive = () => {
		// Single-thumb max-only slider; min axis removed across all filter bars.
		const max = form.querySelector('input[name="max"]')?.value ?? '';
		return max !== '' && Number(max) < 100;
	};
	const isTimeActive = () => !!form.querySelector('input[name="time[]"]:checked');
	const isGenreActive = () => !!form.querySelector('input[name="genre[]"]:checked');

	const updateDateChipActive = () => setTrigger('date', isDateActive());
	const setAllTriggersFromForm = () => {
		updateDateChipActive();
		setTrigger('price', isPriceActive());
		setTrigger('time', isTimeActive());
		setTrigger('genre', isGenreActive());
	};

	// Default: BOTH today and tomorrow active. Never allow both off.
	const toggleDayChip = (key) => {
		const iToday = form.querySelector('input[name="today"]');
		const iTomorrow = form.querySelector('input[name="tomorrow"]');
		const todayOn = (iToday?.value === '1');
		const tomorrowOn = (iTomorrow?.value === '1');

		const clearDateRange = () => {
			const f = form.querySelector('input[name="from"]');
			const t = form.querySelector('input[name="to"]');
			if (f) {
				f.value = '';
			}
			if (t) {
				t.value = '';
			}
			updateDateChipActive();
		};

		if (key === 'booth_today') {
			if (todayOn && !tomorrowOn) {
				return;
			}
			const newState = !todayOn;
			if (iToday) {
				iToday.value = newState ? '1' : '';
			}
			bar.querySelectorAll('[data-filter-toggle="booth_today"]').forEach((b) => b.classList.toggle('is-active', newState));
		} else if (key === 'booth_tomorrow') {
			if (tomorrowOn && !todayOn) {
				return;
			}
			const newState = !tomorrowOn;
			if (iTomorrow) {
				iTomorrow.value = newState ? '1' : '';
			}
			bar.querySelectorAll('[data-filter-toggle="booth_tomorrow"]').forEach((b) => b.classList.toggle('is-active', newState));
		}

		clearDateRange();
	};

	let showSearch = null;
	const filterTracker = createFilterTracker('ticket_booth');

	/**
	 * Reads the current form state and returns a normalised params object
	 * suitable for passing to the REST endpoint.
	 *
	 * @return {Object} Normalised filter parameters.
	 */
	const paramsFromForm = () => {
		const rawObj = getInlineObj(form, MULTI);
		const out = {};

		['q', 'max', 'from', 'to'].forEach((k) => {
			const v = String(rawObj[ k ] || '').trim();
			if (k === 'max') {
				out[ k ] = v;
			} else if (v !== '') {
				out[ k ] = v;
			}
		});

		['today', 'tomorrow', 'access'].forEach((k) => {
			const v = String(rawObj[ k ] || '').trim();
			if (v === '1') {
				out[ k ] = '1';
			}
		});

		['time', 'genre'].forEach((k) => {
			let arr = Array.isArray(rawObj[ k ]) ? rawObj[ k ] : (rawObj[ k ] ? String(rawObj[ k ]).split(',') : []);
			arr = arr.map((s) => String(s).trim()).filter(Boolean);
			if (arr.length) {
				out[ k ] = [...new Set(arr)].sort();
			}
		});

		/*
		 * Read show IDs from the show search module's state, not from FormData.
		 * Double optional chaining is used because showSearch may be null during
		 * the first paramsFromForm call (inside initFilterBar), and getIds may
		 * not be present on all builds.
		 */
		const showIds = showSearch?.getIds?.() ?? [];
		if (showIds.length) {
			out.shows = showIds;
			/*
			 * When shows are selected, free-text search is suppressed. Strip q
			 * here to guard against stale input values at render time.
			 */
			delete out.q;
		}

		const s = normaliseSort(rawObj.sort || 'name_asc');
		if (s !== 'name_asc') {
			out.sort = s;
		}

		return out;
	};

	let isLoadingMore = false;
	let lazyLoadObserver = null;

	const loadMore = async () => {
		if (!sentinel || isLoadingMore) {
			return;
		}

		const total = parseInt(list.dataset.total || '0', 10);
		const offset = parseInt(list.dataset.offset || '0', 10);
		const limit = parseInt(list.dataset.limit || '24', 10);

		if (offset >= total) {
			if (sentinel) {
				sentinel.style.display = 'none';
			}
			if (lazyLoadObserver) {
				lazyLoadObserver.disconnect();
			}
			return;
		}

		isLoadingMore = true;
		if (sentinel) {
			sentinel.classList.add('is-loading');
		}

		/*
		* Snapshot the active filters at the point this request is issued.
		* If the visitor changes the filter bar (for example toggling
		* Today/Tomorrow) while this request is still in flight, the
		* comparison below will no longer match once the response returns.
		*/
		const requestQs = toQS(cleanParams(paramsFromForm(), DEFAULTS));

		try {
			const params = paramsFromForm();
			params.offset = offset;
			params.limit = limit;

			const qs = toQS(cleanParams(params, DEFAULTS));
			const r = await fetch(`${ endpoint }?${ qs }`);
			if (!r.ok) {
				throw new Error('Network response was not ok');
			}

			const data = await r.json();

			/*
			* Discard the response if the filter bar has moved on since this
			* request was made. Appending it now would attach show cards for
			* the previous filter selection underneath the correct, current
			* list, and would overwrite list.dataset with stale pagination
			* values.
			*/
			if (toQS(cleanParams(paramsFromForm(), DEFAULTS)) !== requestQs) {
				return;
			}

			list.insertAdjacentHTML('beforeend', data.html || '');
			initShowCardTabs();

			const newOffset = (data.offset || 0) + (data.limit || 24);
			list.dataset.total = data.total || total;
			list.dataset.offset = newOffset;

			if (newOffset >= (data.total || 0)) {
				if (sentinel) {
					sentinel.style.display = 'none';
				}
				if (lazyLoadObserver) {
					lazyLoadObserver.disconnect();
				}
			}
		} catch (e) {
			console.error('Lazy load error:', e);
			if (lazyLoadObserver) {
				lazyLoadObserver.disconnect();
			}
			if (sentinel) {
				sentinel.style.display = 'none';
			}
		} finally {
			isLoadingMore = false;
			if (sentinel) {
				sentinel.classList.remove('is-loading');
			}
		}
	};

	const resetLazyLoadObserver = (hasNextPage) => {
		if (lazyLoadObserver) {
			lazyLoadObserver.disconnect();
			lazyLoadObserver = null;
		}
		if (!sentinel) {
			return;
		}

		if (hasNextPage) {
			sentinel.style.display = '';
			lazyLoadObserver = new IntersectionObserver((entries) => {
				if (entries[ 0 ].isIntersecting && !isLoadingMore) {
					loadMore();
				}
			}, { rootMargin: '400px 0px' });
			lazyLoadObserver.observe(sentinel);
		} else {
			sentinel.style.display = 'none';
		}
	};

	const render = initFilterBar({
		bar, form, list, endpoint, defaults: DEFAULTS, clearDefaults: CLEAR_DEFAULTS,
		multi: MULTI, paramsFromForm, normaliseSort,
		onBeforeOpenScope(key, { modal, panel }) {
			if (panel) {
				const pKey = panel.getAttribute('data-panel') || '';
				let activeAtOpen = false;

				if (pKey === 'price') {
					const max = form.querySelector('input[name="max"]')?.value ?? '';
					activeAtOpen = max !== '' && Number(max) < 100;
				} else if (pKey === 'time') {
					activeAtOpen = !!form.querySelector('input[name="time[]"]:checked');
				} else if (pKey === 'genre') {
					activeAtOpen = !!form.querySelector('input[name="genre[]"]:checked');
				} else if (pKey === 'date') {
					const f = form.querySelector('input[name="from"]')?.value || '';
					const t = form.querySelector('input[name="to"]')?.value || '';
					activeAtOpen = !!(f || t);
				}

				panel.setAttribute('data-active', activeAtOpen ? '1' : '0');
				snapshotPanel(panel);
				adjustPopoverPosition(panel);
			}

			if (key === 'date' && panel) {
				const container = panel.querySelector('[data-range-calendar="popover"], [data-range-calendar]');
				const from = form.querySelector('input[name="from"]');
				const to = form.querySelector('input[name="to"]');
				if (container && !container.dataset.calBound && from && to) {
					wireCalendarEnable(panel, container, from, to, () => {
						const clearDayChips = () => {
							['booth_today', 'booth_tomorrow'].forEach((k) => {
								bar.querySelectorAll(`[data-filter-toggle="${ k }"]`).forEach((b) => b.classList.remove('is-active'));
							});
							const iToday = form.querySelector('input[name="today"]');
							const iTomorrow = form.querySelector('input[name="tomorrow"]');
							if (iToday) {
								iToday.value = '';
							}
							if (iTomorrow) {
								iTomorrow.value = '';
							}
							updateDateChipActive();
							markScopeDirty(panel, true);
						};
						container.addEventListener('date-selected', clearDayChips);
						from.addEventListener('input', clearDayChips);
						to.addEventListener('input', clearDayChips);
						mountFarloRangeCalendar(container, from, to, 1);
					});
				}
			}

			if (key === 'price' && panel) {
				bindPriceControls(panel);
			}
			if (key === 'all' && modal) {
				bindPriceControls(modal);
			}
		},
		onAfterRender(data, params) {
			filterTracker.track(params);
			paintChoiceActives(bar);
			setAllTriggersFromForm();
			updateAllFiltersCount(bar, form, { multiCsvKeys: MULTI });
			updateClearChip(bar, params, CLEAR_DEFAULTS);
			initShowCardTabs();

			if (!sentinel) {
				return;
			}

			if (data && typeof data === 'object') {
				const total = parseInt(data.total || '0', 10);
				const offset = parseInt(data.offset || '0', 10);
				const limit = parseInt(data.limit || '24', 10);

				list.dataset.total = total;
				list.dataset.offset = offset + limit;
				list.dataset.limit = limit;

				const hasNextPage = (offset + limit) < total;
				resetLazyLoadObserver(hasNextPage);
			} else {
				const total = parseInt(list.dataset.total || '0', 10);
				const offset = parseInt(list.dataset.offset || '0', 10);

				if (total > 0) {
					const hasNextPage = offset < total;
					resetLazyLoadObserver(hasNextPage);
				} else {
					resetLazyLoadObserver(false);
				}
			}
		}
	});

	showSearch = mountShowSearch(bar, form, render);

	bar.addEventListener('click', (e) => {
		const chip = e.target.closest('[data-filter-toggle]');
		if (!chip) {
			return;
		}

		const key = chip.getAttribute('data-filter-toggle');

		if (key === 'booth_today' || key === 'booth_tomorrow') {
			toggleDayChip(key);
			bar.querySelectorAll('[data-panel="date"]').forEach((p) => p.setAttribute('hidden', ''));
			setAllTriggersFromForm();
			return render();
		}

		if (key === 'access') {
			const inp = form.querySelector('input[name="access"]');
			if (inp) {
				inp.value = inp.value === '1' ? '' : '1';
			}
			chip.classList.toggle('is-active');
			setAllTriggersFromForm();
			return render();
		}
	});

	form.querySelectorAll('input[name="from"], input[name="to"]').forEach((i) => {
		i.addEventListener('input', updateDateChipActive);
		i.addEventListener('change', updateDateChipActive);
	});

	bar.addEventListener('click', (e) => {
		const panel = e.target.closest('.popover__panel');
		if (!panel) {
			return;
		}

		const applyBtn = e.target.closest('[data-submit]');
		const clearBtn = e.target.closest('[data-cancel]');
		if (!applyBtn && !clearBtn) {
			return;
		}

		const key = panel.getAttribute('data-panel') || '';

		if (clearBtn) {
			const wasActiveAtOpen = panel.getAttribute('data-active') === '1';
			if (!wasActiveAtOpen) {
				restorePanel(panel);
				panel.setAttribute('hidden', '');
				return;
			}

			if (key === 'price') {
				panel.querySelectorAll('input[name="max"]').forEach((i) => (i.value = ''));
				panel.querySelector('[data-price-wrap]')?.resetPriceUI?.();
				let inlineMax = form.querySelector('input[name="max"]');
				if (!inlineMax) {
					inlineMax = document.createElement('input');
					inlineMax.type = 'hidden';
					inlineMax.name = 'max';
					form.appendChild(inlineMax);
				}
				inlineMax.value = '';
			} else if (key === 'time') {
				panel.querySelectorAll('input[name="time[]"]').forEach((i) => (i.checked = false));
			} else if (key === 'genre') {
				panel.querySelectorAll('input[name="genre[]"]').forEach((i) => (i.checked = false));
			} else if (key === 'date') {
				const inFrom = form.querySelector('input[name="from"]') || (() => {
					const i = document.createElement('input');
					i.type = 'hidden';
					i.name = 'from';
					form.appendChild(i);
					return i;
				})();
				const inTo = form.querySelector('input[name="to"]') || (() => {
					const i = document.createElement('input');
					i.type = 'hidden';
					i.name = 'to';
					form.appendChild(i);
					return i;
				})();
				inFrom.value = '';
				inTo.value = '';
			}

			setAllTriggersFromForm();
			paintChoiceActives(document);
			updateAllFiltersCount(bar, form, { multiCsvKeys: MULTI });
			updateClearChip(bar, paramsFromForm(), CLEAR_DEFAULTS);

			panel.setAttribute('hidden', '');
			render();
			return;
		}

		if (applyBtn) {
			if (key === 'price') {
				const wrap = panel.querySelector('[data-price-wrap]');
				if (wrap) {
					const srcMax = wrap.querySelector('input[name="max"]');
					let inlineMax = form.querySelector('input[name="max"]');
					if (!inlineMax) {
						inlineMax = document.createElement('input');
						inlineMax.type = 'hidden';
						inlineMax.name = 'max';
						form.appendChild(inlineMax);
					}
					inlineMax.value = String(srcMax?.value ?? '');
				}
			}
			setAllTriggersFromForm();
			paintChoiceActives(document);
			updateAllFiltersCount(bar, form, { multiCsvKeys: MULTI });
			updateClearChip(bar, paramsFromForm(), CLEAR_DEFAULTS);

			panel.setAttribute('hidden', '');
			render();
		}
	});

	const hydrateFlagsFromURL = () => {
		const url = new URL(window.location.href);
		const coerce = (v) => {
			const s = String(v || '').trim();
			return s === '1' || /^true|yes|on$/i.test(s);
		};

		const apply = (name, on) => {
			let input = form.querySelector(`input[name="${ name }"]`);
			if (!input) {
				input = document.createElement('input');
				input.type = 'hidden';
				input.name = name;
				form.appendChild(input);
			}
			input.value = on ? '1' : '';
		};

		const hasToday = url.searchParams.has('today');
		const hasTomorrow = url.searchParams.has('tomorrow');

		const urlFrom = (url.searchParams.get('from') || '').trim();
		const urlTo = (url.searchParams.get('to') || '').trim();
		const hasDateRange = urlFrom !== '' || urlTo !== '';

		let t, tm;

		if (hasDateRange) {
			t = false;
			tm = false;
		} else if (!hasToday && !hasTomorrow) {
			t = true;
			tm = false;
		} else {
			t = coerce(url.searchParams.get('today'));
			tm = coerce(url.searchParams.get('tomorrow'));

			if (!t && !tm) {
				t = true;
				tm = false;
			}
		}

		const ac = coerce(url.searchParams.get('access'));

		apply('today', t);
		apply('tomorrow', tm);
		apply('access', ac);

		bar.querySelectorAll('[data-filter-toggle="booth_today"]').forEach((b) => b.classList.toggle('is-active', t));
		bar.querySelectorAll('[data-filter-toggle="booth_tomorrow"]').forEach((b) => b.classList.toggle('is-active', tm));
		bar.querySelectorAll('[data-filter-toggle="access"]').forEach((b) => b.classList.toggle('is-active', ac));

		if (hasDateRange) {
			const f = form.querySelector('input[name="from"]');
			const to = form.querySelector('input[name="to"]');
			if (f) {
				f.value = urlFrom;
			}
			if (to) {
				to.value = urlTo;
			}
			updateDateChipActive();
		} else if (t || tm) {
			const f = form.querySelector('input[name="from"]');
			const to = form.querySelector('input[name="to"]');
			if (f) {
				f.value = '';
			}
			if (to) {
				to.value = '';
			}
			updateDateChipActive();
		}
	};

	window.addEventListener('popstate', () => {
		hydrateFlagsFromURL();
		paintChoiceActives(document);
		setAllTriggersFromForm();
		updateAllFiltersCount(bar, form, { multiCsvKeys: MULTI });
		updateClearChip(bar, paramsFromForm(), CLEAR_DEFAULTS);
		render();
	});

	hydrateFlagsFromURL();
	paintChoiceActives(document);
	setAllTriggersFromForm();
	updateAllFiltersCount(bar, form, { multiCsvKeys: MULTI });
	updateClearChip(bar, paramsFromForm(), CLEAR_DEFAULTS);

	// Baseline after URL flag hydration and show-search mount, so the default
	// "today" flag and any pre-selected shows are not reported as new applies.
	filterTracker.init(paramsFromForm());

	const hasSSR = list?.dataset?.ssrInitial === '1' && (list.firstElementChild || (list.textContent || '').trim().length > 0);
	const u = new URL(location.href);
	const hasParams = (u.search || '').length > 1;

	if (!hasSSR || hasParams) {
		render();
	} else {
		const initialTotal = parseInt(list.dataset.total || '0', 10);
		const initialOffset = parseInt(list.dataset.offset || '0', 10);
		resetLazyLoadObserver(initialOffset < initialTotal);
	}
}
